/**
 * Pong Game
 * A classic arcade game where two paddles hit a ball back and forth.
 * Player controls the left paddle, AI controls the right paddle.
 */

import { getHighScores, type HighScoreEntry } from '../state/highscore'

// ============================================================================
// Types
// ============================================================================

/** Callback function called when game ends */
export type GameOverCallback = (score: number) => void

interface Paddle {
  x: number
  y: number
  width: number
  height: number
  speed: number
}

interface Ball {
  x: number
  y: number
  radius: number
  velocityX: number
  velocityY: number
}

interface GameState {
  playerPaddle: Paddle
  aiPaddle: Paddle
  ball: Ball
  playerScore: number
  aiScore: number
  gameOver: boolean
  paused: boolean
  started: boolean
  winner: 'player' | 'ai' | null
  canvasWidth: number
  canvasHeight: number
}

interface InputState {
  up: boolean
  down: boolean
}

// ============================================================================
// Constants
// ============================================================================

const PADDLE_WIDTH = 12
const PADDLE_HEIGHT = 80
const PADDLE_SPEED = 6
const PADDLE_MARGIN = 20

const BALL_RADIUS = 8
const BALL_INITIAL_SPEED = 5
const BALL_SPEED_INCREMENT = 0.5
const BALL_MAX_SPEED = 12

const AI_REACTION_SPEED = 4
const AI_ERROR_MARGIN = 30 // AI won't perfectly track the ball

const WINNING_SCORE = 7

/** Game version */
export const PONG_VERSION = '1.0.0'

// ============================================================================
// Game Logic (Exported for Testing)
// ============================================================================

/**
 * Create a paddle at the specified position
 */
export function createPaddle(x: number, canvasHeight: number): Paddle {
  return {
    x,
    y: canvasHeight / 2 - PADDLE_HEIGHT / 2,
    width: PADDLE_WIDTH,
    height: PADDLE_HEIGHT,
    speed: PADDLE_SPEED,
  }
}

/**
 * Create the ball at the center of the canvas
 */
export function createBall(canvasWidth: number, canvasHeight: number): Ball {
  // Random direction for initial serve
  const direction = Math.random() > 0.5 ? 1 : -1
  const angle = ((Math.random() * 60 - 30) * Math.PI) / 180 // -30 to 30 degrees

  return {
    x: canvasWidth / 2,
    y: canvasHeight / 2,
    radius: BALL_RADIUS,
    velocityX: Math.cos(angle) * BALL_INITIAL_SPEED * direction,
    velocityY: Math.sin(angle) * BALL_INITIAL_SPEED,
  }
}

/**
 * Reset the ball after a point is scored
 */
export function resetBall(canvasWidth: number, canvasHeight: number, serveDirection: 1 | -1): Ball {
  const angle = ((Math.random() * 60 - 30) * Math.PI) / 180

  return {
    x: canvasWidth / 2,
    y: canvasHeight / 2,
    radius: BALL_RADIUS,
    velocityX: Math.cos(angle) * BALL_INITIAL_SPEED * serveDirection,
    velocityY: Math.sin(angle) * BALL_INITIAL_SPEED,
  }
}

/**
 * Move a paddle based on input, clamping to canvas bounds
 */
export function movePaddle(paddle: Paddle, direction: number, canvasHeight: number): Paddle {
  const newY = paddle.y + direction * paddle.speed

  return {
    ...paddle,
    y: Math.max(0, Math.min(canvasHeight - paddle.height, newY)),
  }
}

/**
 * Update AI paddle to track the ball using trajectory prediction
 */
export function updateAiPaddle(paddle: Paddle, ball: Ball, canvasHeight: number): Paddle {
  const paddleCenter = paddle.y + paddle.height / 2

  // Predict where the ball will intersect with the paddle line
  const predictedY = predictBallIntersection(ball, paddle.x, canvasHeight)

  // Add small random error for imperfection (reduced from AI_ERROR_MARGIN)
  const errorMargin = AI_ERROR_MARGIN * 0.35 // Much less error since we're predicting
  const targetY = predictedY + (Math.random() - 0.5) * errorMargin

  let direction = 0
  if (paddleCenter < targetY - 5) {
    // Smaller dead zone for more responsiveness
    direction = 1
  } else if (paddleCenter > targetY + 5) {
    direction = -1
  }

  const newY = paddle.y + direction * AI_REACTION_SPEED

  return {
    ...paddle,
    y: Math.max(0, Math.min(canvasHeight - paddle.height, newY)),
  }
}

/**
 * Predict where the ball will intersect with the AI paddle line
 * Accounts for wall bounces and ball trajectory
 */
export function predictBallIntersection(ball: Ball, paddleX: number, canvasHeight: number): number {
  // If ball is moving away from AI (towards player), don't predict
  if (ball.velocityX <= 0) {
    return ball.y
  }

  let predictedX = ball.x
  let predictedY = ball.y
  const velocityX = ball.velocityX
  let velocityY = ball.velocityY

  // Keep simulating until ball reaches paddle X position
  const maxIterations = 100 // Prevent infinite loops
  const iterations = 0

  while (iterations < maxIterations) {
    // Calculate distance to paddle
    const distanceToPaddle = paddleX - predictedX

    // If we're past the paddle X (due to floating point), break
    if (distanceToPaddle <= 0) {
      break
    }

    // Calculate time to reach paddle at current velocity
    const timeToPaddle = distanceToPaddle / velocityX

    // Calculate Y position after this time
    const newY = predictedY + velocityY * timeToPaddle

    // Check if we hit top or bottom wall before reaching paddle
    if (newY - ball.radius <= 0) {
      // Hit top wall
      const timeToWall = (ball.radius - predictedY) / velocityY
      const wallX = predictedX + velocityX * timeToWall

      // If we hit wall before reaching paddle, bounce and continue
      if (wallX < paddleX) {
        predictedX = wallX
        predictedY = ball.radius
        velocityY = -velocityY // Bounce
        continue
      }
    } else if (newY + ball.radius >= canvasHeight) {
      // Hit bottom wall
      const timeToWall = (canvasHeight - ball.radius - predictedY) / velocityY
      const wallX = predictedX + velocityX * timeToWall

      // If we hit wall before reaching paddle, bounce and continue
      if (wallX < paddleX) {
        predictedX = wallX
        predictedY = canvasHeight - ball.radius
        velocityY = -velocityY // Bounce
        continue
      }
    }

    // No wall hit, ball reaches paddle directly
    predictedY = newY
    break
  }

  // Clamp to canvas bounds (shouldn't be necessary but safety check)
  return Math.max(ball.radius, Math.min(canvasHeight - ball.radius, predictedY))
}

/**
 * Check if the ball collides with a paddle
 */
export function checkPaddleCollision(ball: Ball, paddle: Paddle): boolean {
  return (
    ball.x - ball.radius < paddle.x + paddle.width &&
    ball.x + ball.radius > paddle.x &&
    ball.y - ball.radius < paddle.y + paddle.height &&
    ball.y + ball.radius > paddle.y
  )
}

/**
 * Calculate the ball's new velocity after hitting a paddle
 */
export function calculateBounceVelocity(
  ball: Ball,
  paddle: Paddle
): { velocityX: number; velocityY: number } {
  // Calculate where on the paddle the ball hit (normalized -1 to 1)
  const hitPosition = (ball.y - (paddle.y + paddle.height / 2)) / (paddle.height / 2)

  // Calculate current speed
  const currentSpeed = Math.sqrt(ball.velocityX ** 2 + ball.velocityY ** 2)

  // Increase speed slightly, up to max
  const newSpeed = Math.min(currentSpeed + BALL_SPEED_INCREMENT, BALL_MAX_SPEED)

  // Calculate new angle based on hit position (max 60 degrees)
  const maxAngle = (60 * Math.PI) / 180
  const angle = hitPosition * maxAngle

  // Reverse X direction and apply new angle
  const direction = ball.velocityX > 0 ? -1 : 1

  return {
    velocityX: Math.cos(angle) * newSpeed * direction,
    velocityY: Math.sin(angle) * newSpeed,
  }
}

/**
 * Update the ball position and handle collisions
 */
export function updateBall(
  ball: Ball,
  playerPaddle: Paddle,
  aiPaddle: Paddle,
  canvasWidth: number,
  canvasHeight: number
): { ball: Ball; scored: 'player' | 'ai' | null } {
  const newBall = { ...ball }
  let scored: 'player' | 'ai' | null = null

  // Update position
  newBall.x += newBall.velocityX
  newBall.y += newBall.velocityY

  // Wall collision (top and bottom)
  if (newBall.y - newBall.radius <= 0) {
    newBall.y = newBall.radius
    newBall.velocityY = -newBall.velocityY
  } else if (newBall.y + newBall.radius >= canvasHeight) {
    newBall.y = canvasHeight - newBall.radius
    newBall.velocityY = -newBall.velocityY
  }

  // Paddle collision
  if (checkPaddleCollision(newBall, playerPaddle)) {
    const newVelocity = calculateBounceVelocity(newBall, playerPaddle)
    newBall.velocityX = Math.abs(newVelocity.velocityX) // Ensure going right
    newBall.velocityY = newVelocity.velocityY
    newBall.x = playerPaddle.x + playerPaddle.width + newBall.radius
  } else if (checkPaddleCollision(newBall, aiPaddle)) {
    const newVelocity = calculateBounceVelocity(newBall, aiPaddle)
    newBall.velocityX = -Math.abs(newVelocity.velocityX) // Ensure going left
    newBall.velocityY = newVelocity.velocityY
    newBall.x = aiPaddle.x - newBall.radius
  }

  // Scoring (ball goes past paddle)
  if (newBall.x - newBall.radius <= 0) {
    scored = 'ai'
  } else if (newBall.x + newBall.radius >= canvasWidth) {
    scored = 'player'
  }

  return { ball: newBall, scored }
}

/**
 * Create the initial game state
 */
export function createGameState(canvasWidth: number, canvasHeight: number): GameState {
  return {
    playerPaddle: createPaddle(PADDLE_MARGIN, canvasHeight),
    aiPaddle: createPaddle(canvasWidth - PADDLE_MARGIN - PADDLE_WIDTH, canvasHeight),
    ball: createBall(canvasWidth, canvasHeight),
    playerScore: 0,
    aiScore: 0,
    gameOver: false,
    paused: false,
    started: false,
    winner: null,
    canvasWidth,
    canvasHeight,
  }
}

/**
 * Update the game state for one frame
 */
export function updateGame(state: GameState, input: InputState): GameState {
  if (state.gameOver || state.paused || !state.started) {
    return state
  }

  // Update player paddle
  let playerDirection = 0
  if (input.up) playerDirection = -1
  if (input.down) playerDirection = 1

  const newPlayerPaddle = movePaddle(state.playerPaddle, playerDirection, state.canvasHeight)

  // Update AI paddle
  const newAiPaddle = updateAiPaddle(state.aiPaddle, state.ball, state.canvasHeight)

  // Update ball
  const { ball: newBall, scored } = updateBall(
    state.ball,
    newPlayerPaddle,
    newAiPaddle,
    state.canvasWidth,
    state.canvasHeight
  )

  // Handle scoring
  let newPlayerScore = state.playerScore
  let newAiScore = state.aiScore
  let newGameOver: boolean = state.gameOver
  let newWinner: 'player' | 'ai' | null = state.winner
  let finalBall = newBall

  if (scored) {
    if (scored === 'player') {
      newPlayerScore++
    } else {
      newAiScore++
    }

    // Check for winner
    if (newPlayerScore >= WINNING_SCORE) {
      newGameOver = true
      newWinner = 'player'
    } else if (newAiScore >= WINNING_SCORE) {
      newGameOver = true
      newWinner = 'ai'
    } else {
      // Reset ball, serve towards the player who just scored
      const serveDirection = scored === 'player' ? -1 : 1
      finalBall = resetBall(state.canvasWidth, state.canvasHeight, serveDirection as 1 | -1)
    }
  }

  return {
    ...state,
    playerPaddle: newPlayerPaddle,
    aiPaddle: newAiPaddle,
    ball: finalBall,
    playerScore: newPlayerScore,
    aiScore: newAiScore,
    gameOver: newGameOver,
    winner: newWinner,
  }
}

// ============================================================================
// Rendering
// ============================================================================

function drawCenterLine(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.strokeStyle = 'rgba(100, 100, 100, 0.3)'
  ctx.lineWidth = 2
  ctx.setLineDash([10, 10])

  ctx.beginPath()
  ctx.moveTo(canvasWidth / 2, 0)
  ctx.lineTo(canvasWidth / 2, canvasHeight)
  ctx.stroke()

  ctx.setLineDash([])
}

function drawPaddle(ctx: CanvasRenderingContext2D, paddle: Paddle): void {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(paddle.x, paddle.y, paddle.width, paddle.height)
}

function drawBall(ctx: CanvasRenderingContext2D, ball: Ball): void {
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2)
  ctx.fill()
}

function drawScore(
  ctx: CanvasRenderingContext2D,
  playerScore: number,
  aiScore: number,
  canvasWidth: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '48px "Press Start 2P", monospace'
  ctx.textAlign = 'center'

  // Player score (left side)
  ctx.fillText(String(playerScore), canvasWidth / 4, 60)

  // AI score (right side)
  ctx.fillText(String(aiScore), (canvasWidth * 3) / 4, 60)
}

function drawStartScreen(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  highScores: HighScoreEntry[]
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '48px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('PONG', canvasWidth / 2, canvasHeight / 2 - 120)

  ctx.fillStyle = '#ffffff'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('First to 7 points wins!', canvasWidth / 2, canvasHeight / 2 - 70)

  // Show high scores if available
  if (highScores.length > 0) {
    ctx.fillStyle = '#f97316' // orange
    ctx.font = '10px "Press Start 2P", monospace'
    ctx.fillText('HIGH SCORES', canvasWidth / 2, canvasHeight / 2 - 35)

    ctx.fillStyle = '#9ca3af' // gray
    ctx.font = '9px "Press Start 2P", monospace'
    const topScores = highScores.slice(0, 3)
    topScores.forEach((entry, index) => {
      const y = canvasHeight / 2 - 10 + index * 18
      ctx.fillText(
        `${index + 1}. ${entry.name}  ${entry.score.toLocaleString()}`,
        canvasWidth / 2,
        y
      )
    })
  }

  const controlsY = highScores.length > 0 ? canvasHeight / 2 + 55 : canvasHeight / 2 + 20

  ctx.fillStyle = '#22c55e' // Green
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('CONTROLS', canvasWidth / 2, controlsY)

  ctx.fillStyle = '#9ca3af' // Gray
  ctx.font = '10px "Press Start 2P", monospace'
  ctx.fillText('Arrow Up/Down or W/S - Move Paddle', canvasWidth / 2, controlsY + 25)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Press SPACE to start', canvasWidth / 2, controlsY + 70)
}

function drawGameOver(
  ctx: CanvasRenderingContext2D,
  winner: 'player' | 'ai' | null,
  playerScore: number,
  aiScore: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  const winnerText = winner === 'player' ? 'YOU WIN!' : 'AI WINS!'
  const winnerColor = winner === 'player' ? '#22c55e' : '#ef4444'

  ctx.fillStyle = winnerColor
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText(winnerText, canvasWidth / 2, canvasHeight / 2 - 40)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`${playerScore} - ${aiScore}`, canvasWidth / 2, canvasHeight / 2 + 20)

  ctx.fillStyle = '#f97316' // Orange
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 60)
}

// ============================================================================
// Main Game Loop
// ============================================================================

/**
 * Initialize and run the Pong game
 * @param container The HTML element to render the game in
 * @param onGameOver Optional callback called when game ends with the final score
 * @returns Cleanup function to stop the game
 */
export function loadPong(container: HTMLElement, onGameOver?: GameOverCallback): () => void {
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  container.innerHTML = ''
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get canvas context')
    return (): void => {}
  }

  // Store reference to avoid null checks in nested functions
  const context: CanvasRenderingContext2D = ctx

  // Track if game over callback has been called
  let gameOverCallbackFired = false

  // Resize canvas to match container
  function resizeCanvas(): void {
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
  }
  resizeCanvas()

  const resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(container)

  // Initialize game state
  let state = createGameState(canvas.width, canvas.height)

  // Input state
  const input: InputState = {
    up: false,
    down: false,
  }

  let running = true

  // Input handlers
  function handleKeyDown(e: KeyboardEvent): void {
    if (state.gameOver) {
      if (e.key === 'Enter') {
        // Restart game
        state = createGameState(canvas.width, canvas.height)
        gameOverCallbackFired = false
      }
      return
    }

    if (!state.started) {
      if (e.key === ' ') {
        state.started = true
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowUp':
      case 'w':
        input.up = true
        e.preventDefault()
        break
      case 'ArrowDown':
      case 's':
        input.down = true
        e.preventDefault()
        break
    }
  }

  function handleKeyUp(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowUp':
      case 'w':
        input.up = false
        break
      case 'ArrowDown':
      case 's':
        input.down = false
        break
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)

  // Game loop
  function gameLoop(): void {
    if (!running) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    context.fillStyle = '#0a0a0a'
    context.fillRect(0, 0, width, height)

    // Show start screen if game hasn't started
    if (!state.started) {
      const highScores = getHighScores('pong')
      drawStartScreen(context, width, height, highScores)
      requestAnimationFrame(gameLoop)
      return
    }

    // Track previous state
    const previousGameOver = state.gameOver

    // Update game state
    state = updateGame(state, input)

    // Fire game over callback once when game ends (only if player wins)
    if (state.gameOver && !previousGameOver && !gameOverCallbackFired && onGameOver) {
      // Only submit score if player won
      if (state.winner === 'player') {
        gameOverCallbackFired = true
        onGameOver(state.playerScore)
      }
    }

    // Draw game elements
    drawCenterLine(context, width, height)
    drawPaddle(context, state.playerPaddle)
    drawPaddle(context, state.aiPaddle)
    drawBall(context, state.ball)
    drawScore(context, state.playerScore, state.aiScore, width)

    if (state.gameOver) {
      drawGameOver(context, state.winner, state.playerScore, state.aiScore, width, height)
    }

    requestAnimationFrame(gameLoop)
  }

  // Start the game loop
  gameLoop()

  // Return cleanup function
  return (): void => {
    running = false
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
    resizeObserver.disconnect()
    container.innerHTML = ''
  }
}
